import React, { useEffect, useState } from 'react'
import { Modal, Form } from 'semantic-ui-react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { X, Eye, EyeClosed } from 'phosphor-react'
import { FlexRowBetween } from './Common'
import { TitleText } from './Text'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { AuthService } from 'utility'
import { useAuth } from 'context/user'
import { useNotification } from 'hooks/toast'

const PasswordField = styled(Form.Field)`
    .relative {
        position: relative;
    }
    .password-icon {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        cursor: pointer;
        color: #666;
        display: flex;
        align-items: center;
    }
    input {
        padding-right: 40px !important;
    }
`

export const ChangePasswordModal = ({ open, onClose, user }) => {
    const { successToast, errorToast } = useNotification()
    const { logout } = useAuth()
    const navigate = useNavigate()
    const [userName, setUserName] = useState('')
    const [oldPassword, setOldPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [showOldPassword, setShowOldPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (open) {
            setUserName(user?.Email || user?.UserName || '')
            setOldPassword('')
            setNewPassword('')
            setShowOldPassword(false)
            setShowNewPassword(false)
            setLoading(false)
        }
    }, [open, user])

    const handleClose = () => {
        if (loading) return
        setOldPassword('')
        setNewPassword('')
        setShowOldPassword(false)
        setShowNewPassword(false)
        onClose()
    }

    const getErrorMessage = error => {
        const data = error?.error
        if (!data) return null
        if (typeof data === 'string') return data
        return (
            data.ErrorMessage ||
            data.Message ||
            data.message ||
            data.ExceptionMessage ||
            (data.ModelState &&
                Object.values(data.ModelState).flat().filter(Boolean)[0]) ||
            null
        )
    }

    const handleSave = async () => {
        if (!userName.trim()) {
            errorToast('User name is required')
            return
        }
        if (!oldPassword.trim()) {
            errorToast('Old password is required')
            return
        }
        if (!newPassword.trim()) {
            errorToast('New password is required')
            return
        }

        try {
            setLoading(true)
            const result = await AuthService.updatePassword({
                UserName: userName.trim(),
                OldPassword: oldPassword,
                NewPassword: newPassword,
            })
            if (result?.Success === false) {
                errorToast(
                    result.ErrorMessage ||
                        result.Message ||
                        'Failed to update password',
                )
                return
            }
            successToast('Password updated successfully. Please sign in again.')
            onClose()
            await AuthService.logout()
            logout()
            const dashboardId = user?.DashboardStyle?.DashboardId
            navigate(dashboardId === 2 ? '/sign-in-franchising' : '/sign-in')
        } catch (error) {
            errorToast(
                getErrorMessage(error) ||
                    'Failed to update password. Please check your old password and try again.',
            )
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={handleClose} size="small" className="model">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Change Password</TitleText>
                    <X className="pointer" size={32} onClick={handleClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>User Name</label>
                        <Form.Input
                            value={userName}
                            readOnly
                            placeholder="User name"
                        />
                    </Form.Field>
                    <PasswordField>
                        <label>Old Password</label>
                        <div className="relative ui fluid input">
                            <input
                                type={showOldPassword ? 'text' : 'password'}
                                value={oldPassword}
                                onChange={e => setOldPassword(e.target.value)}
                                autoComplete="current-password"
                                placeholder="Enter old password"
                            />
                            <div
                                className="password-icon"
                                onClick={() => setShowOldPassword(prev => !prev)}
                            >
                                {showOldPassword ? (
                                    <Eye size={20} />
                                ) : (
                                    <EyeClosed size={20} />
                                )}
                            </div>
                        </div>
                    </PasswordField>
                    <PasswordField>
                        <label>New Password</label>
                        <div className="relative ui fluid input">
                            <input
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                placeholder="Enter new password"
                            />
                            <div
                                className="password-icon"
                                onClick={() => setShowNewPassword(prev => !prev)}
                            >
                                {showNewPassword ? (
                                    <Eye size={20} />
                                ) : (
                                    <EyeClosed size={20} />
                                )}
                            </div>
                        </div>
                    </PasswordField>
                </Form>
            </Modal.Content>
            <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <AppSecondaryButton onClick={handleClose} disabled={loading}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton onClick={handleSave} loading={loading} disabled={loading}>
                    Save
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
